import { useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router'
import { useLocalStorage } from 'react-use'

import { useUserOrganization } from '@modules-deprecated/app/organization/userOrganizationContext'
import { useUmbrellaStatus } from '@modules-deprecated/app/umbrellas/hooks/useUmbrellaStatus'

import { LocalStorageKeys } from '../../../enums/localStorageKeys'
import { useOrganizationViewUrl } from '../../../hooks/routing/useOrganizationViewUrl'
import { useOrganizationSource } from '../../../hooks/useOrganizationSource'
import { reactRoute } from '../../../utils/routing/reactRoute'
import { OnboardingPlatform } from '../enums/onboardingPlatform'
import { OnboardingVariantType } from '../types'
import { isEligibleForOnboarding } from '../utils/isEligibleForOnboarding'
import { isOnboardingFinalized } from '../utils/isOnboardingFinalized'
import { useOnboardingStepPersistence } from './useOnboardingStepPersistence'

export const useOnboardingDashboardRedirect = (variant?: OnboardingVariantType) => {
  const navigate = useNavigate()
  const hasRedirectedRef = useRef(false)
  const { url: dashboardUrl } = useOrganizationViewUrl(reactRoute.organization.dashboard.getMainRoute())
  const { organization } = useUserOrganization()
  const { isSourceBilly } = useOrganizationSource()
  const { hasUmbrellas: isUmbrellaMember } = useUmbrellaStatus()
  const { isFetched, onboardingCurrentStep, onboardingPlatform } = useOnboardingStepPersistence()
  const [onboardingFinalizedLocalStorage] = useLocalStorage(
    `${LocalStorageKeys.OnboardingFinalized}-${organization?.id ?? ''}`,
  )

  const isWebPlatform = !onboardingPlatform || onboardingPlatform === OnboardingPlatform.Web

  const shouldRedirect = isFetched && !!variant && !!dashboardUrl && !hasRedirectedRef.current
  const isNotEligibleForOnboarding =
    !isWebPlatform || !isEligibleForOnboarding({ isSourceBilly, isUmbrellaMember, organization })

  // Redirect when onboarding is finalized — either from localStorage cache (instant, before
  // org settings resolve) or from org settings once fetched. hasRedirectedRef prevents double redirect.
  useEffect(() => {
    const isFinalizedFromLocalStorage = !!organization?.id && onboardingFinalizedLocalStorage === true
    const isFinalizedFromOrganizationSettings = !!onboardingCurrentStep && isOnboardingFinalized(onboardingCurrentStep)

    if (
      dashboardUrl &&
      !hasRedirectedRef.current &&
      (isFinalizedFromLocalStorage || isFinalizedFromOrganizationSettings)
    ) {
      hasRedirectedRef.current = true
      navigate(dashboardUrl)
    }
  }, [dashboardUrl, navigate, onboardingCurrentStep, onboardingFinalizedLocalStorage, organization?.id])

  // Redirect users who are not eligible for onboarding
  useEffect(() => {
    if (shouldRedirect && isNotEligibleForOnboarding) {
      hasRedirectedRef.current = true
      navigate(dashboardUrl)
    }
  }, [dashboardUrl, navigate, isNotEligibleForOnboarding, shouldRedirect])

  // Call this before updateCurrentStep() in finalizeOnboarding to prevent the finalization
  // effect above from overriding the eligibility-based redirect
  const markAsRedirected = useCallback(() => {
    hasRedirectedRef.current = true
  }, [])

  return { markAsRedirected }
}
